import { useMemo, useState } from 'react'
import { Download, PackageCheck, Truck, WalletCards } from 'lucide-react'
import { PageHeader } from '@/components/page-header'
import { DataTable } from '@/components/data-table'
import { StatusBadge } from '@/components/status-badge'
import { PaginationBar } from '@/components/pagination-bar'
import { OrderDetailDrawer } from '@/components/order-detail-drawer'
import type { OrderDetail } from '@/components/order-detail-drawer'
import { Button } from '@/components/ui/button'
import { DataLoadError } from '@/components/data-load-error'
import { DataUnavailable } from '@/components/data-unavailable'
import { useOrdersData } from '@/hooks/use-orders-data'
import type { StudioReviewUnavailableReasonCode } from '@/hooks/dashboard-data-state'
import type { StudioReviewDataClient } from '@/services/dashboard-api'
import { useStudioStore } from '@/store/studio-store'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { toast } from '@/store/toast-store'
import type { OrderRecord } from '@/types/dashboard'
import { DashboardContainer } from '../components/dashboard-container'
import { OperationsTabs } from '../components/operations-tabs'
import { ReviewObservationDisclosure } from '../components/review-observation-disclosure'
import {
  activityExportBlockedReason,
  downloadActivityCsv,
} from '../lib/activity-export'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

type ActivityCopy = StudioDictionary['businessScreens']['activity']

/**
 * The status filter is keyed by a STABLE token, not by its own label.
 *
 * It used to be keyed by the English word (`STATUS_MAP['Applied']`), so the
 * moment the tab read `반영됨` the lookup returned `undefined` and the filter
 * silently showed everything. The token is the state; the label is the word.
 */
const STATUS_TABS = [
  { key: 'all', status: '' },
  { key: 'pending', status: 'pending' },
  { key: 'applied', status: 'shipping' },
  { key: 'reviewed', status: 'completed' },
  { key: 'rejected', status: 'refunded' },
] as const satisfies readonly {
  key: keyof ActivityCopy['tabs']
  status: OrderRecord['status'] | ''
}[]

const PAGE_SIZE = 10

/** This screen renames three badge states — a shipped order is an APPLIED
 *  change here — so it passes its own labels rather than taking the badge's. */
function statusLabelsFor(copy: ActivityCopy): Partial<Record<OrderRecord['status'], string>> {
  return {
    shipping: copy.tabs.applied,
    completed: copy.tabs.reviewed,
    refunded: copy.tabs.rejected,
  }
}

/** Column headers are words on the screen, so they follow the active locale
 *  rather than being frozen at module scope. */
function columnsFor(copy: ActivityCopy) {
  const statusLabels = statusLabelsFor(copy)
  return [
    { key: 'id' as const, header: '#', width: '80px' },
    { key: 'customer_name' as const, header: copy.columns.actor },
    { key: 'app_name' as const, header: copy.columns.artifact },
    { key: 'amount' as const, header: copy.columns.changes, align: 'right' as const, render: (v: unknown) => copy.changeCount((v as number).toLocaleString()) },
    { key: 'status' as const, header: copy.columns.state, render: (v: unknown) => {
      const status = v as OrderRecord['status']
      return <StatusBadge status={status} label={statusLabels[status]} />
    } },
    { key: 'created_at' as const, header: copy.columns.updated, align: 'right' as const },
  ]
}

interface OrdersPageProps {
  dataClient?: StudioReviewDataClient
}

export function OrdersPage({ dataClient }: OrdersPageProps = {}) {
  const { data: orders, status, reviewState, observedAt, evidenceRefs, unavailableReasonCode, unavailableReason, reload } = useOrdersData(dataClient)
  const [statusTab, setStatusTab] = useState<(typeof STATUS_TABS)[number]['key']>('all')
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null)
  const [page, setPage] = useState(1)
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.businessScreens.activity
  const reviewCopy = strings.businessScreens.review
  const pageState = status === 'ready' ? reviewState ?? 'error' : status
  const measuredOrders = pageState === 'measured' ? orders as OrderRecord[] : null
  const filtered = useMemo(() => {
    if (measuredOrders === null) return null
    const selectedStatus = STATUS_TABS.find((tab) => tab.key === statusTab)?.status
    if (!selectedStatus) return measuredOrders
    return measuredOrders.filter((order) => order.status === selectedStatus)
  }, [measuredOrders, statusTab])
  const totalPages = filtered === null ? null : Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered === null ? null : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const exportBlockedReason = pageState === 'measured' && filtered !== null
    ? activityExportBlockedReason('ready', filtered.length, copy.blocked)
    : pageState === 'empty'
      ? copy.blocked.none
      : pageState === 'error'
        ? copy.blocked.notLoaded
        : pageState === 'loading'
          ? copy.blocked.loading
          : copy.blocked.unavailable

  function handleExport() {
    if (filtered === null) return
    const filename = downloadActivityCsv(filtered)
    toast.success(copy.exported(filtered.length, filename))
  }

  function goToShare() {
    setStudioRoute('apps-live')
    pushStudioRoute('apps-live')
  }

  let content: React.ReactNode
  switch (pageState) {
    case 'loading':
      content = (
        <div className="animate-pulse space-y-4" role="status">
          <p className="text-sm text-muted-foreground">{copy.loading}</p>
          <div className="h-48 rounded-xl bg-muted" aria-hidden="true" />
        </div>
      )
      break
    case 'measured': {
      const measuredRows = measuredOrders as OrderRecord[]
      const visibleRows = paged as OrderRecord[]
      const measuredTotalPages = totalPages as number
      const summary = {
        pending: measuredRows.filter((order) => order.status === 'pending').length,
        shipping: measuredRows.filter((order) => order.status === 'shipping').length,
        completed: measuredRows.filter((order) => order.status === 'completed').length,
      }
      content = (
        <>
          <ReviewObservationDisclosure observedAt={observedAt as string} evidenceRefs={evidenceRefs} scope={copy.scope} />
          <section className="grid gap-3 sm:grid-cols-3" aria-label={copy.summaryLabel}>
            {[
              [copy.tiles.pending, summary.pending, WalletCards],
              [copy.tiles.applied, summary.shipping, Truck],
              [copy.tiles.reviewed, summary.completed, PackageCheck],
            ].map(([label, value, Icon]) => {
              const IconComponent = Icon as typeof PackageCheck
              return (
                <div key={label as string} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label as string}</span>
                    <IconComponent className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-foreground">{value as number}</div>
                </div>
              )
            })}
          </section>
          <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1 shadow-sm">
            {STATUS_TABS.map((tab) => (
              <button key={tab.key} onClick={() => { setStatusTab(tab.key); setPage(1) }}
                className={`h-10 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition-colors ${statusTab === tab.key ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                {copy.tabs[tab.key]}
              </button>
            ))}
          </div>
          <DataTable columns={columnsFor(copy)} data={visibleRows}
            onRowClick={(row) => {
              setSelectedOrder({
                id: row.id,
                customer_name: row.customer_name,
                customer_email: row.customer_email,
                app_name: row.app_name,
                status: row.status,
                items: [],
                total: row.amount,
                currency: row.currency,
                created_at: row.created_at,
              })
            }}
          />
          <PaginationBar page={page} totalPages={measuredTotalPages} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )
      break
    }
    case 'empty':
      content = (
        <div className="rounded-xl border border-dashed border-border bg-card p-6" role="status">
          <p className="text-sm font-medium text-foreground">{copy.empty}</p>
          <ReviewObservationDisclosure observedAt={observedAt as string} evidenceRefs={evidenceRefs} scope={copy.scope} state="empty" />
        </div>
      )
      break
    case 'unavailable': {
      const guidance = copy.unavailable[unavailableReasonCode as StudioReviewUnavailableReasonCode]
      content = (
        <>
          <DataUnavailable summary={guidance.summary} message={unavailableReason as string} testId="orders-unavailable" />
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">{guidance.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{guidance.detail}</p>
            <Button variant="outline" size="sm" className="mt-4 min-h-10" onClick={goToShare}>{reviewCopy.openSurface(strings.navigation.share)}</Button>
          </section>
        </>
      )
      break
    }
    case 'error':
      content = <DataLoadError message={copy.loadFailed} onRetry={reload} testId="orders-error" />
      break
  }

  return (
    <DashboardContainer className="py-6 space-y-6">
      <OperationsTabs activeRoute="orders" />
      <PageHeader
        route="orders"
        title={strings.navigation.activity}
        description={copy.description}
        action={(
          <div className="space-y-1.5">
            <Button
              variant="outline"
              size="lg"
              className="px-3 text-sm"
              data-testid="orders-export"
              disabled={exportBlockedReason !== null}
              onClick={exportBlockedReason === null ? handleExport : undefined}
              aria-label={exportBlockedReason === null ? copy.exportAria(String(filtered?.length)) : copy.exportBlockedAria(exportBlockedReason)}
              title={exportBlockedReason ?? undefined}
            >
              <Download className="h-4 w-4 mr-1" />{copy.exportLabel}
            </Button>
            {exportBlockedReason !== null && (
              <p className="text-xs leading-5 text-muted-foreground" data-testid="orders-export-reason">
                {exportBlockedReason}
              </p>
            )}
          </div>
        )}
      />
      {content}
      <OrderDetailDrawer order={selectedOrder} onClose={() => setSelectedOrder(null)} />
    </DashboardContainer>
  )
}
